import { useEffect, useMemo } from 'react';

import { useThree } from '@react-three/fiber';
import { BaseControls, NormalizedPointer } from '@spazious/dollhouse-three';

export interface ConnectControlsHookOptions {
  makeDefault?: boolean;
  domElement?: HTMLElement;
}

/**
 * Attaches the drag controls to the events of the context
 */
export const useConnectControls = <
  T extends abstract new (...args: ConstructorParameters<typeof BaseControls>) => InstanceType<
    typeof BaseControls
  >
>(
  ControlsImpl: new (...args: ConstructorParameters<T>) => InstanceType<T>,
  opts?: ConnectControlsHookOptions
): InstanceType<T> => {
  const events = useThree(({ events }) => events);
  const camera = useThree(({ camera }) => camera);
  const gl = useThree(({ gl }) => gl);
  const get = useThree(({ get }) => get);
  const set = useThree(({ set }) => set);
  const { makeDefault = false, domElement } = { ...opts };
  const explDomElement = (domElement || events.connected || gl.domElement) as HTMLElement;

  // @ts-ignore
  const controls = useMemo(() => new ControlsImpl(camera), [ControlsImpl, camera]);

  useEffect(() => {
    controls.connect(explDomElement, new NormalizedPointer(gl.domElement));
    return () => {
      controls.disconnect();
    };
  }, [controls, explDomElement, gl.domElement]);

  useEffect(() => {
    if (makeDefault) {
      const old = get().controls;
      set({ controls: controls as any });
      return () => set({ controls: old });
    }
  }, [makeDefault, controls, get, set]);

  useEffect(() => {});

  return controls;
};
